/**
 * Contador de conversaciones sin leer para el badge del menú (OpenSpec 022 C7).
 *
 * Mismo patrón que la campanita (`DropMenuAlert`): fetch al montar + socket + refresh al reconectar.
 *
 * **El servidor manda una señal, no el número.** El total depende de a qué canales tiene acceso cada
 * operador, así que dos personas del mismo store pueden ver distinto: el backend avisa "recalculá" y
 * cada cliente pide el suyo. Ver `emitUnreadChanged()` del gateway para por qué no se puede empujar
 * el número por una room.
 *
 * El refetch va **debounced**: una ráfaga de mensajes no puede disparar una llamada por mensaje.
 */
import { useEffect, useRef, useState } from "react";
import { io, Socket } from "socket.io-client";
import {
  CONVERSATIONS_API_URL,
  conversationsApi,
  getAccessToken,
  isConversationsConfigured,
} from "@/services/conversations.api";

const REFETCH_DEBOUNCE_MS = 800;

export function useUnreadConversations(): number {
  const [unread, setUnread] = useState(0);
  const timer = useRef<ReturnType<typeof setTimeout> | null>(null);

  useEffect(() => {
    // Sin URL del conv-service no hay a dónde conectarse. Esto vive en el sider, o sea que corre en
    // TODA pantalla: sin el guard, cada carga abriría un socket y un GET contra el propio admin.
    if (!isConversationsConfigured()) return;

    let alive = true;
    const refresh = () => {
      conversationsApi
        .unreadConversations()
        .then((n) => {
          if (alive) setUnread(n);
        })
        .catch(() => {});
    };
    const refreshDebounced = () => {
      if (timer.current) clearTimeout(timer.current);
      timer.current = setTimeout(refresh, REFETCH_DEBOUNCE_MS);
    };

    refresh();
    const socket: Socket = io(`${CONVERSATIONS_API_URL}/conversations`, {
      transports: ["websocket"],
      auth: { token: getAccessToken() },
    });
    socket.on("conversation:unread-changed", refreshDebounced);
    // Reconexión: pudo cambiar mientras estuvimos caídos.
    socket.on("connect", refresh);

    return () => {
      alive = false;
      if (timer.current) clearTimeout(timer.current);
      socket.disconnect();
    };
  }, []);

  return unread;
}
